/**
 * Real User Monitoring (RUM) Integration - Spans + Logs
 *
 * Integrates Datadog RUM with the unified OpenTelemetry observability system.
 * Provides RUM-APM correlation for end-to-end visibility.
 *
 * Thread Safety & Race Condition Prevention:
 * - Uses proper synchronization to prevent double initialization
 * - Handles concurrent initialization attempts safely
 * - Provides atomic initialization state checks
 *
 * Environment Detection Strategy:
 * - NODE_ENV: Used for development/test detection and feature flags
 * - VERCEL_ENV: Used for deployment environment classification in telemetry data
 * - This separation allows proper behavior across all deployment scenarios
 */

import { datadogLogs } from '@datadog/browser-logs';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { logger } from '@magicschool/logger';
import type { MagicSchoolUser } from '@magicschool/supabase/user';
import { getBuildPrefix } from '@magicschool/utils/nextjs/metadata';
import { redactAuthTokens } from '@/util/redactAuthTokens';

// Initialization state management with race condition prevention
let rumInitializationState: 'not-started' | 'initializing' | 'completed' | 'failed' = 'not-started';
let logsInitializationState: 'not-started' | 'initializing' | 'completed' | 'failed' = 'not-started';

// Promises to handle concurrent initialization attempts
let rumInitializationPromise: Promise<void> | null = null;
let logsInitializationPromise: Promise<void> | null = null;

/**
 * Environment detection for RUM with detailed reasoning
 */
function getRUMEnvironmentContext() {
  const nodeEnv = process.env.NODE_ENV;
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const shortSHA = getBuildPrefix(); // first 7 characters of process.env.VERCEL_GIT_COMMIT_SHA;

  return {
    // NODE_ENV-based detection (build-time and development behavior)
    isTest: nodeEnv === 'test',
    isDevelopment: nodeEnv === 'development',

    // CI/CD detection
    isCI: process.env.CI === 'true' || process.env.GITHUB_ACTIONS === 'true',

    // Browser environment detection (more comprehensive than just HeadlessChrome)
    isHeadless:
      typeof window !== 'undefined' &&
      (window.navigator.userAgent.includes('HeadlessChrome') ||
        window.navigator.webdriver === true ||
        (window as unknown as { navigator?: { webdriver?: boolean } }).navigator?.webdriver === true),

    // VERCEL_ENV provides runtime deployment context
    // Use this for telemetry environment classification
    deploymentEnv: vercelEnv === 'production' ? 'prod' : 'dev',
    currentEnv: process.env.NEXT_PUBLIC_DD_ENV || vercelEnv || 'dev',
    serviceVersion: shortSHA || process.env.VERCEL_GIT_COMMIT_SHA || process.env.NEXT_PUBLIC_DD_VERSION || 'unknown',
    // Force enable flag for development testing
    forceEnabled: process.env.NEXT_PUBLIC_DATADOG_RUM_FORCE_ENABLE === 'true',
  };
}

/**
 * Check if RUM should be enabled with comprehensive environment detection
 */
export function shouldEnableRUM(): boolean {
  const env = getRUMEnvironmentContext();

  // Disable in preview deployments to avoid polluting production data
  if (env.currentEnv.startsWith('preview')) return true;

  // Disable in all test environments to prevent timeouts and interference
  if (env.isTest || env.isCI || env.isHeadless) {
    return false;
  }

  // Disable in development unless explicitly forced
  // Reasoning: Development environment generates noisy data and can impact performance
  if (env.isDevelopment && !env.forceEnabled) {
    return false;
  }

  // Check if required configuration is available
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

  return !!(applicationId && clientToken);
}

/**
 * Get RUM configuration with low-cardinality tags and proper environment detection
 */
export function getRUMConfig(user?: Partial<MagicSchoolUser>): RumInitConfiguration | null {
  // Check if RUM should be enabled
  if (!shouldEnableRUM()) {
    return null;
  }

  // Ensure required environment variables are present
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const site = (process.env.NEXT_PUBLIC_DATADOG_SITE as 'datadoghq.com' | undefined) || 'datadoghq.com';

  if (!applicationId || !clientToken) {
    logger.warn('[RUM] Missing required configuration', {
      hasAppId: !!applicationId,
      hasClientToken: !!clientToken,
    });
    return null;
  }

  const env = getRUMEnvironmentContext();

  // Map plan numbers to low-cardinality readable names
  // Keeping only essential plan categories to avoid high cardinality
  const planName = user?.plan === 3 ? 'PARTNER' : user?.plan === 2 ? 'PLUS' : 'FREE';

  const config: RumInitConfiguration = {
    applicationId,
    clientToken,
    site,
    service: process.env.NEXT_PUBLIC_DD_SERVICE || 'magicschoolai',

    // Use consistent environment detection strategy
    // VERCEL_ENV for deployment classification, fallback to NODE_ENV for local dev
    env: process.env.NEXT_PUBLIC_DD_ENV || env.deploymentEnv,

    // prefer the short SHA as a consistent Version tag
    version: env.serviceVersion || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || process.env.NEXT_PUBLIC_DD_VERSION || 'unknown',

    // Session sampling rate optimized for cost and coverage
    // Production: 30% for cost efficiency while maintaining visibility
    // Development: 100% for complete debugging capability
    sessionSampleRate: process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE
      ? Number.parseInt(process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE, 10)
      : env.deploymentEnv === 'prod'
        ? 30
        : 100,

    // Session replay disabled for privacy
    sessionReplaySampleRate: 0,

    // Start with tracking consent withheld so RUM does not write the _dd_s
    // session cookie until the user grants analytics consent. Consent is
    // granted/revoked at runtime by CookieYes via DatadogUserContext, which
    // calls datadogRum.setTrackingConsent(). This mirrors the Browser Logs init.
    trackingConsent: 'not-granted',

    // Start session manually after checking user
    startSessionReplayRecordingManually: true,

    // Performance tracking configuration
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Default privacy level
    defaultPrivacyLevel: 'mask',

    // User context optimized for low cardinality and privacy
    // Only include essential user categorization data that helps with debugging
    // without creating excessive unique tag combinations
    ...(user?.id && {
      user: {
        id: user.id,
        plan: planName, // LOW cardinality: only 3 values (FREE/PLUS/PARTNER)
        // Removed plan_id as it duplicates planName with higher cardinality
        // Removed subscription_plan as it can have many unique values
        // Removed org_id as it creates high cardinality
        user_role: user.user_role || 'teacher', // LOW cardinality: limited role types
        is_demo: user.is_demo ? 'true' : 'false', // LOW cardinality: boolean
      },
    }),

    // 2025 Best Practice: Use W3C Trace Context for RUM-APM correlation
    // This allows RUM traces to connect with backend APM traces.
    // Allow trace context propagation to all dev and main domains used by MagicSchool.
    // This matches all preview, login, teacher, and app subdomains for both magicschool.ai and magicschooldev.com.
    // If you add new domains, update this list for full RUM-APM correlation coverage.
    allowedTracingUrls: [
      { match: window.location.origin, propagatorTypes: ['tracecontext'] },
      // Preview deployments (vercel)
      { match: /^https:\/\/magicschoolai-git-.*\.vercel\.app/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/teacher-.*\.vercel\.app/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/teacher-.*-magicschoolai-vercel-app\.translate\.goog/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/login-.*\.vercel\.app/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/login-.*-magicschoolai-vercel-app\.translate\.goog/, propagatorTypes: ['tracecontext'] },
      // Main and dev domains
      { match: /^https:\/\/app\.magicschooldev\.com/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/login\.magicschooldev\.com/, propagatorTypes: ['tracecontext'] },
      // If you have production domains like app.magicschool.ai, add them here as well:
      { match: /^https:\/\/app\.magicschool\.ai/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/login\.magicschool\.ai/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/teacher\.magicschool\.ai/, propagatorTypes: ['tracecontext'] },
      // Optionally, match all subdomains of magicschool.ai and magicschooldev.com for future-proofing:
      { match: /^https:\/\/.*\.magicschool\.ai/, propagatorTypes: ['tracecontext'] },
      { match: /^https:\/\/.*\.magicschooldev\.com/, propagatorTypes: ['tracecontext'] },
    ],

    // Enable experimental features for better APM correlation
    enableExperimentalFeatures: ['clickmap'],

    // Filters out browser extensions, third-party scripts, and common noise
    beforeSend: (event) => {
      event.view.url = redactAuthTokens(event.view.url);
      if (typeof event.view.referrer === 'string') event.view.referrer = redactAuthTokens(event.view.referrer);
      if (event.error && typeof event.error === 'object' && 'message' in event.error) {
        const errorMessage = redactAuthTokens(String(event.error.message));
        event.error.message = errorMessage;

        for (const pattern of ignorePatterns) {
          if (errorMessage.includes(pattern)) return false;
        }

        for (const pattern of ignoreRegexPatterns) {
          if (pattern.test(errorMessage)) return false;
        }
      }

      if (event.error && typeof event.error === 'object' && 'stack' in event.error && event.error.stack) {
        const stack = redactAuthTokens(String(event.error.stack));
        event.error.stack = stack;
        for (const pattern of denyUrlPatterns) {
          if (pattern.test(stack)) return false;
        }
      }
      return true;
    },
  };

  return config;
}

const ignorePatterns = [
  // ResizeObserver errors
  'ResizeObserver loop limit exceeded',
  'InvalidStateError: Cannot close a closed AudioContext.',

  // Random plugins/extensions
  'top.GLOBALS',
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',

  // Facebook errors
  'fb_xd_fragment',

  // ISP "optimizing" proxy
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',

  // Conduit toolbar
  'conduitPage',

  // Random network errors
  'TypeError: Failed to fetch',
  'TypeError: Load failed',

  // Webpack errors
  "Can't find variable: webpackJsonp",

  // Next.js internal errors we can't control
  "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
  "Cannot read properties of null (reading 'removeChild')",
  "Cannot read properties of null (reading 'ownerDocument')",
  "Cannot read properties of undefined (reading 'parentElement')",
  "undefined is not an object (evaluating 't.parentElement')",
  'Node.removeChild: The node to be removed is not a child of this node',
  "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",

  // Parser errors
  "Unexpected token '<'",

  // React errors
  'Maximum update depth exceeded',

  // Permission/device errors
  'NotFoundError: Requested device not found',

  // Legacy browser errors
  'ReferenceError: structuredClone is not defined',
  "Can't find variable: structuredClone",
];

const ignoreRegexPatterns = [
  /^NotAllowedError:.*/, // User denied permission (microphone, etc.)
  /Can't find variable: GM_.*/, // GreaseMonkey extension errors
  /^jb$/, // Random untrackable error
];

const denyUrlPatterns = [
  // Facebook
  /graph\.facebook\.com/i,
  /connect\.facebook\.net\/en_US\/all\.js/i,

  // Woopra analytics
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,

  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,

  // Other plugins/extensions
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  /api-iam\.intercom\.io/i,

  // Adobe SDK
  /adobe/i,
];

/**
 * Initialize Datadog RUM with proper race condition handling
 */
export async function initDatadogRUM(user?: Partial<MagicSchoolUser>): Promise<void> {
  // Client-side only
  if (typeof window === 'undefined') {
    return;
  }

  // Handle concurrent initialization attempts with atomic state management
  if (rumInitializationState === 'completed') {
    return;
  }

  if (rumInitializationState === 'initializing' && rumInitializationPromise) {
    // Wait for ongoing initialization to complete
    await rumInitializationPromise;
    return;
  }

  if (rumInitializationState === 'failed') {
    // Don't retry failed initialization to avoid loops
    return;
  }

  // Start initialization atomically
  rumInitializationState = 'initializing';

  // Create initialization promise before starting work to handle race conditions
  rumInitializationPromise = performRUMInitialization(user);

  try {
    await rumInitializationPromise;
    rumInitializationState = 'completed';

    logger.debug('[RUM] Initialization completed successfully', {
      userId: user?.id,
      hasUser: !!user?.id,
    });
  } catch (error) {
    rumInitializationState = 'failed';

    logger.error('[RUM] Initialization failed and will not retry', {
      error: error instanceof Error ? error.message : String(error),
      userId: user?.id,
    });

    // Don't re-throw to prevent uncaught promise rejections
    // The failed state prevents retries
  } finally {
    // Clear the promise reference to allow garbage collection
    rumInitializationPromise = null;
  }
}

/**
 * Internal RUM initialization logic
 */
function performRUMInitialization(user?: Partial<MagicSchoolUser>): Promise<void> {
  return new Promise((resolve, reject) => {
    const config = getRUMConfig(user);
    if (!config) {
      resolve();
      return;
    }

    try {
      datadogRum.init(config);

      // Only start session replay if explicitly enabled (currently disabled for privacy)
      if ((config.sessionReplaySampleRate ?? 0) > 0) {
        datadogRum.startSessionReplayRecording();
      }

      logger.info('[RUM] Initialized with thread-safe initialization', {
        service: config.service,
        env: config.env,
        version: config.version,
        userId: user?.id,
        sessionSampleRate: config.sessionSampleRate,
        replayEnabled: (config.sessionReplaySampleRate ?? 0) > 0,
      });

      resolve();
    } catch (error) {
      logger.error('[RUM] Failed to initialize', {
        error: error instanceof Error ? error.message : String(error),
        userId: user?.id,
      });
      reject(error);
    }
  });
}

/**
 * Initialize Datadog Browser Logs with race condition protection
 */
export async function initDatadogLogs(): Promise<void> {
  // Client-side only
  if (typeof window === 'undefined') {
    return;
  }

  // Handle concurrent initialization attempts with atomic state management
  if (logsInitializationState === 'completed') {
    return;
  }

  if (logsInitializationState === 'initializing' && logsInitializationPromise) {
    // Wait for ongoing initialization to complete
    await logsInitializationPromise;
    return;
  }

  if (logsInitializationState === 'failed') {
    // Don't retry failed initialization to avoid loops
    return;
  }

  if (!shouldEnableRUM()) {
    return;
  }

  // Start initialization atomically
  logsInitializationState = 'initializing';

  // Create initialization promise before starting work to handle race conditions
  logsInitializationPromise = performLogsInitialization();

  try {
    await logsInitializationPromise;
    logsInitializationState = 'completed';

    logger.debug('[Browser Logs] Initialization completed successfully');
  } catch (error) {
    logsInitializationState = 'failed';

    logger.error('[Browser Logs] Initialization failed and will not retry', {
      error: error instanceof Error ? error.message : String(error),
    });

    // Don't re-throw to prevent uncaught promise rejections
    // The failed state prevents retries
  } finally {
    // Clear the promise reference to allow garbage collection
    logsInitializationPromise = null;
  }
}

/**
 * Internal logs initialization logic
 */
function performLogsInitialization(): Promise<void> {
  return new Promise((resolve, reject) => {
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    const site = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com';

    if (!clientToken) {
      logger.warn('[Browser Logs] Missing client token');
      resolve();
      return;
    }

    const env = getRUMEnvironmentContext();

    try {
      datadogLogs.init({
        clientToken,
        site: site as 'datadoghq.com',
        service: process.env.NEXT_PUBLIC_DD_SERVICE || 'magicschoolai',
        env: process.env.NEXT_PUBLIC_DD_ENV || env.deploymentEnv,
        version: env.serviceVersion || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || process.env.NEXT_PUBLIC_DD_VERSION || 'unknown',
        beforeSend: (event) => {
          event.message = redactAuthTokens(event.message);
          if (event.view?.url) event.view.url = redactAuthTokens(event.view.url);
          if (event.error?.stack) event.error.stack = redactAuthTokens(event.error.stack);
          return true;
        },
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['log', 'info', 'warn', 'error'],

        // Use consistent sampling rate with RUM
        sessionSampleRate: process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE
          ? Number.parseInt(process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE, 10)
          : env.deploymentEnv === 'prod'
            ? 30
            : 100,

        silentMultipleInit: true,
        // This is granted/revoked by CookieYes
        trackingConsent: 'not-granted',
      });

      logger.info('[Browser Logs] Initialized with thread-safe initialization', {
        env: env.deploymentEnv,
        service: process.env.NEXT_PUBLIC_DD_SERVICE || 'magicschoolai',
      });

      resolve();
    } catch (error) {
      logger.error('[Browser Logs] Failed to initialize', {
        error: error instanceof Error ? error.message : String(error),
      });
      reject(error);
    }
  });
}

/**
 * Set user context for RUM with low-cardinality tags
 */
export function setDatadogUser(user: Partial<MagicSchoolUser>): void {
  if (rumInitializationState !== 'completed' || !user.id) {
    return;
  }

  // Use low-cardinality user categorization
  const planName = user.plan === 3 ? 'PARTNER' : user.plan === 2 ? 'PLUS' : 'FREE';

  const userData = {
    id: user.id,
    plan: planName, // LOW cardinality: only 3 values
    user_role: user.user_role || 'teacher', // LOW cardinality: limited role types
    is_demo: user.is_demo ? 'true' : 'false', // LOW cardinality: boolean
    // Removed high-cardinality fields: org_id, plan_id, subscription_plan
  };

  try {
    datadogRum.setUser(userData);

    // Only set logs user if logs are also initialized
    if (logsInitializationState === 'completed') {
      datadogLogs.setUser({
        id: user.id,
        plan: planName,
      });
    }
  } catch (error) {
    logger.warn('[RUM] Failed to set user context', {
      error: error instanceof Error ? error.message : String(error),
      userId: user.id,
    });
  }
}

/**
 * Clear user context for RUM and Logs
 */
export function clearDatadogUser(): void {
  try {
    if (rumInitializationState === 'completed') {
      datadogRum.clearUser();
    }

    if (logsInitializationState === 'completed') {
      datadogLogs.clearUser();
    }
  } catch (error) {
    logger.warn('[RUM] Failed to clear user context', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Check if RUM is initialized and ready
 */
export function isDatadogRUMInitialized(): boolean {
  return rumInitializationState === 'completed';
}

/**
 * Check if Browser Logs is initialized and ready
 */
export function isDatadogLogsInitialized(): boolean {
  return logsInitializationState === 'completed';
}

/**
 * Track custom RUM actions with low-cardinality context
 */
export function trackRUMAction(name: string, context?: Record<string, unknown>): void {
  if (rumInitializationState !== 'completed') {
    return;
  }

  try {
    // Ensure context doesn't contain high-cardinality values
    const sanitizedContext = context ? sanitizeContextForLowCardinality(context) : undefined;
    datadogRum.addAction(name, sanitizedContext);
  } catch (error) {
    logger.warn('[RUM] Failed to track action', {
      error: error instanceof Error ? error.message : String(error),
      actionName: name,
    });
  }
}

/**
 * Track custom RUM errors with essential context
 */
export function trackRUMError(error: Error, context?: Record<string, unknown>): void {
  if (rumInitializationState !== 'completed') {
    return;
  }

  try {
    // Ensure context doesn't contain high-cardinality values
    const sanitizedContext = context ? sanitizeContextForLowCardinality(context) : undefined;
    datadogRum.addError(error, sanitizedContext);
  } catch (trackingError) {
    logger.warn('[RUM] Failed to track error', {
      error: trackingError instanceof Error ? trackingError.message : String(trackingError),
      originalError: error.message,
    });
  }
}

/**
 * Start a custom RUM duration vital with low-cardinality context.
 *
 * Vitals track end-to-end timings (e.g. time-to-first-token) that don't fit the
 * per-view Core Web Vitals model. Call `stopRUMDurationVital` with the same name
 * to complete it; an unstopped vital is simply never sent, so it's safe to start
 * one and abandon it if the flow errors or aborts before it would naturally stop.
 */
export function startRUMDurationVital(name: string, context?: Record<string, unknown>): void {
  if (rumInitializationState !== 'completed') {
    return;
  }

  try {
    const sanitizedContext = context ? sanitizeContextForLowCardinality(context) : undefined;
    datadogRum.startDurationVital(name, sanitizedContext ? { context: sanitizedContext } : undefined);
  } catch (error) {
    logger.warn('[RUM] Failed to start duration vital', {
      error: error instanceof Error ? error.message : String(error),
      vitalName: name,
    });
  }
}

/**
 * Stop a custom RUM duration vital started with `startRUMDurationVital`.
 */
export function stopRUMDurationVital(name: string, context?: Record<string, unknown>): void {
  if (rumInitializationState !== 'completed') {
    return;
  }

  try {
    const sanitizedContext = context ? sanitizeContextForLowCardinality(context) : undefined;
    datadogRum.stopDurationVital(name, sanitizedContext ? { context: sanitizedContext } : undefined);
  } catch (error) {
    logger.warn('[RUM] Failed to stop duration vital', {
      error: error instanceof Error ? error.message : String(error),
      vitalName: name,
    });
  }
}

/**
 * Sanitize context object to remove high-cardinality values and prevent cardinality explosion
 *
 * This function is critical for preventing Datadog cost increases due to high-cardinality tags.
 * It removes fields that could create unlimited unique tag combinations.
 */
export function sanitizeContextForLowCardinality(context: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  // List of allowed keys that have proven low cardinality
  const lowCardinalityAllowList = new Set([
    'tool_id', // Limited set of tools
    'user_id', // Used for debugging specific users
    'feature', // Limited set of feature names
    'action_type', // Limited set of action types
    'status', // Limited status values (success/error/loading)
    'error_type', // Limited error categories
    'plan', // Limited plan types (FREE/PLUS/PARTNER)
    'user_role', // Limited role types
    'is_demo', // Boolean
    'chat_surface', // Limited set: raina/tool/student/insights/preview
    'message_count_bucket', // Bucketed thread length: 0/1-5/6-20/21-50/51+
    'elapsed_bucket', // Bucketed turn duration: 0-1s/1-3s/3-10s/10-30s/30s+
  ]);

  for (const [key, value] of Object.entries(context)) {
    // Only include explicitly allowed keys
    if (lowCardinalityAllowList.has(key)) {
      // Ensure the value itself is not high-cardinality
      sanitized[key] = sanitizeValue(value, key);
    }

    // Skip all other keys to prevent accidental high-cardinality data
  }

  // Log if we filtered out keys to help developers understand what's being dropped
  const droppedKeys = Object.keys(context).filter((key) => !lowCardinalityAllowList.has(key));
  if (droppedKeys.length > 0) {
    logger.debug('[RUM] Sanitized high-cardinality context keys', {
      droppedKeys,
      keptKeys: Object.keys(sanitized),
    });
  }

  return sanitized;
}

/**
 * Sanitize individual values to ensure they don't create high cardinality
 */
function sanitizeValue(value: unknown, key?: string): unknown {
  if (typeof value === 'string') {
    // Don't sanitize explicitly allowed debugging fields
    if (key === 'user_id' || key === 'tool_id') {
      return value;
    }

    // Truncate very long strings that might be unique
    if (value.length > 50) {
      return `${value.slice(0, 47)}...`;
    }

    // Check for UUID pattern specifically (36 chars with dashes or 32 without)
    if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value) || /^[a-f0-9]{32}$/i.test(value)) {
      return '[UUID-LIKE]';
    }

    if (/^\d{10,}$/.test(value)) {
      return '[TIMESTAMP-LIKE]'; // Timestamps or long numbers
    }

    return value;
  }

  if (typeof value === 'number') {
    // Very large numbers might be timestamps or unique IDs
    if (value > 1000000000) {
      return '[LARGE-NUMBER]';
    }
    return value;
  }

  // Handle objects and arrays
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return `[ARRAY(${value.length})]`;
    }
    return '[OBJECT]';
  }

  // For other types, convert to string and sanitize
  if (value != null) {
    return sanitizeValue(String(value), key);
  }

  return value;
}
